import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { Pencil, Trash } from '@phosphor-icons/react';
import { useState, useRef, useEffect } from 'react';
import type { View } from 'types/views';
import { Text } from 'components';

type SortableViewItemProps = {
  view: View;
  isSelected: boolean;
  isEditing: boolean;
  onSelect: (viewId: number) => void;
  onEdit: (view: View) => void;
  onDelete: (viewId: number) => void;
  onSaveEdit: (viewId: number, newName: string) => void;
  onEditValueChange: (newValue: string) => void;
  onEditValidationError?: () => void;
};

export const SortableViewItem = ({
  view,
  isSelected,
  isEditing,
  onSelect,
  onEdit,
  onDelete,
  onSaveEdit,
  onEditValueChange,
  onEditValidationError,
}: SortableViewItemProps) => {
  const theme = useTheme();
  const [editValue, setEditValue] = useState(view.viewName);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(view.viewName);
  }, [view.viewName]);

  const handleSaveEdit = () => {
    // If nothing changed, just close edit mode without any action
    if (editValue === view.viewName) {
      onEdit(view);
      return;
    }

    // Only validate if user actually changed the value
    if (!editValue.trim()) {
      onEditValidationError?.();
      setEditValue(view.viewName);
    } else {
      onSaveEdit(view.id, editValue.trim());
      onEdit(view); // Close edit mode immediately after save
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();

    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      setEditValue(view.viewName);
      onEdit(view);
    }
  };

  return (
    <ItemContainer
      isSelected={isSelected}
      data-view-id={view.id}
      onClick={() => !isEditing && onSelect(view.id)}
    >
      <ContentSection>
        {isEditing ? (
          <ViewNameInput
            ref={inputRef}
            data-editing-view="true"
            value={editValue}
            onChange={e => {
              setEditValue(e.target.value);
              onEditValueChange(e.target.value);
            }}
            onBlur={handleSaveEdit}
            onKeyDown={handleKeyDown}
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <Text variant="labelSm" title={view.viewName} noWrap>
            {view.viewName}
          </Text>
        )}
      </ContentSection>
      <InlineActions className="inlineActions">
        <ActionButton
          onClick={e => {
            e.stopPropagation();
            onEdit(view);
          }}
        >
          <Pencil size={16} color={theme.palette.text.secondary} />
        </ActionButton>
        <ActionButton
          onClick={e => {
            e.stopPropagation();
            onDelete(view.id);
          }}
        >
          <Trash size={16} color={theme.palette.text.secondary} />
        </ActionButton>
      </InlineActions>
    </ItemContainer>
  );
};

const ItemContainer = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${p => p.theme.s.md};
  margin: ${p => p.theme.s.xxs};
  padding: ${p => p.theme.s.xs};
  height: 44px;
  border-radius: ${p => p.theme.s.xxs};
  cursor: pointer;
  background-color: ${p => (p.isSelected ? p.theme.palette.bg.brandSubtle : 'transparent')};
  text-shadow: ${p => (p.isSelected ? '0 0 0.5px currentColor' : 'none')};

  &:hover {
    background-color: ${p => p.theme.palette.utility.grey[50]};

    .inlineActions {
      opacity: 1;
    }
  }
`;

const ContentSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.s.xs};
  overflow: hidden;
  min-width: 0;
  flex: 1;
`;

const ViewNameInput = styled.input`
  flex: 1;
  font-size: ${p => p.theme.typography.labelSm.fontSize};
  color: ${p => p.theme.palette.text.primary};
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
  font-family: inherit;
  font-weight: inherit;
  min-width: 0;
  max-width: 100%;
  width: 100%;
`;

const InlineActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.2s;
  flex-shrink: 0;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: ${p => p.theme.s.xxs};
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: ${p => p.theme.palette.bg.primaryHover};
  }
`;
